// Draft state for the onboarding ritual. Local-only — no database writes.
// SECURITY: matter_text is sensitive free text. Never log draft contents.

export interface OnboardingDraft {
  decision_latency?: string;
  life_domain?: string;
  matter_text?: string;
  blocker?: string;
  lens?: string;
  decide_by?: string;
}


const STORAGE_KEY = "outspoken_onboarding_v1";

export function loadDraft(): OnboardingDraft {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return parsed as OnboardingDraft;
  } catch {
    return {};
  }
}

export function saveDraft(partial: Partial<OnboardingDraft>): OnboardingDraft {
  const next = { ...loadDraft(), ...partial };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Private browsing / storage disabled — keep the in-memory value only.
  }
  return next;
}

export function clearDraft(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // no-op
  }
}

/** True when the user has answered at least one onboarding question. */
export function hasDraftAnswers(draft: OnboardingDraft): boolean {
  return Boolean(
    draft.decision_latency ||
      draft.life_domain ||
      draft.matter_text?.trim() ||
      draft.blocker ||
      draft.lens
  );
}

/**
 * First unanswered screen for a partially filled draft, so a returning user
 * resumes where they left off instead of restarting at screen 01.
 */
export function resumeStepFromDraft(draft: OnboardingDraft): number {
  if (!draft.decision_latency) return 2;
  if (!draft.life_domain) return 3;
  if (!draft.matter_text?.trim()) return 4;
  if (!draft.blocker) return 6;
  if (!draft.lens) return 8;
  if (!draft.decide_by) return 9;
  return 11;
}

/** Session-scoped "Skip for now" dismissal, so the redirect can't become a trap. */
const SKIP_KEY = "outspoken_onboarding_skipped";

export function markOnboardingSkipped(): void {
  try {
    sessionStorage.setItem(SKIP_KEY, "1");
  } catch {
    // no-op
  }
}

export function isOnboardingSkipped(): boolean {
  try {
    return sessionStorage.getItem(SKIP_KEY) === "1";
  } catch {
    return false;
  }
}
